import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  FaClipboardList,
  FaPhoneAlt,
  FaPlus,
  FaSave,
  FaTimes,
  FaUserCheck,
} from "react-icons/fa";
import "./RecopiladorPracticaTsPage.css";
import { getApiUrl } from "../../utils/apiUrl";
import { getCsrfToken } from "../../utils/getCsrfToken";

const pautaPasos = [
  "Buscar a la maestra en Intranet de RedMaestra",
  'Revisar sección "cursos": para conocer el programa en el que participó, año de egreso, comuna y cualquier información relevante que permita contextualizar el llamado.',
  "Leer comentarios para rescatar información valiosa de la maestra",
  "Realizar llamado telefónico. De no poder contactarla, registrar intentos en página correspondiente.",
  'Revisar las secciones "Información Personal" y "Trayectoria", verificando que los datos registrados se encuentren completos y actualizados. Completarlos en caso de ser necesario.',
  'Registrar en casilla "Comentarios" en la Intranet cualquier información nueva o relevante entregada por la maestra, especialmente relacionada con situación laboral actual, desarrollo del oficio, emprendimientos, interés en participar en actividades de la Comunidad RedMaestra',
];

const pautaScript = [
  {
    titulo: "Inicio del llamado",
    texto:
      "Hola, ¿hablo con [Nombre]?\nMi nombre es ______, estudiante en práctica de Trabajo Social y te llamo de RedMaestra. ¿Cómo estás?\nTe contacto porque estamos realizando una actualización de información de las maestras de nuestra Comunidad RedMaestra, para mantener nuestros registros al día y seguir fortaleciendo este espacio.\nAntes de continuar, quería comentarte que toda la información que nos entregues será tratada de manera confidencial y utilizada únicamente para mantener actualizados nuestros registros y fortalecer el acompañamiento de la Comunidad RedMaestra.\nTienes unos minutos para conversar? ¿A través de qué medio te acomoda tener esta conversación?",
  },
  {
    titulo: "Actualización de la información",
    texto:
      "Quería aprovechar de preguntarte algunas cosas para actualizar tus datos.\nOBSERVACIÓN: Las preguntas que se realicen dependerán de la información en la Intranet.\nSi aparece toda la información: ¿Sigues viviendo en...? ¿Tu número de teléfono y correo electrónico siguen siendo los mismos?",
  },
  {
    titulo: 'Vinculación (registrar respuestas en "Comentarios")',
    texto:
      "Actualmente,\n1. ¿Te encuentras trabajando? ¿En qué área?\n2. ¿Has podido desempeñarte en el oficio o utilizar los conocimientos adquiridos? ¿Por qué?\n\nSi la respuesta es NO, registrar una de las opciones disponibles.\n\n3. ¿Qué crees que necesitarías para comenzar o desarrollarte en el oficio?\n\nSi la respuesta es SI:\n4. ¿Hay algún logro, proyecto o experiencia que te gustaría compartir con nosotras?\n5. Durante el último año, ¿aproximadamente cuántos trabajos has realizado relacionados con el oficio?\n\nAdemás, queremos saber si te gustaría seguir participando en actividades, capacitaciones o encuentros organizados por la Comunidad RedMaestra.",
  },
  {
    titulo: "Cierre y agradecimiento",
    texto: "Muchas gracias por tu tiempo y por compartir esta información con nosotras. Que tengas un buen día.",
  },
];

const emptyForm = {
  maestra: "",
  estado: "Pendiente",
  fecha_contacto: "",
  trabaja_oficio: "",
  trabaja_oficio_no: [],
  necesidad: "",
  trabajos_oficio: "",
  intranet_actualizada: "NO",
  comentarios: "",
  asignada_a: "",
};

const normalizeString = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const optionList = (options, key) => options?.[key] || [];

const formatMaestraOptionLabel = (maestra) => {
  const completion =
    typeof maestra.completion_percentage === "number"
      ? `[${maestra.completion_percentage}%] `
      : "";

  return `${completion}${maestra.nombre || maestra.rut} - ${
    maestra.tipo || "Sin tipo"
  }`;
};

const getSelectableMaestras = (options, selectedRegistro) => {
  const maestras = optionList(options, "maestras");

  if (!selectedRegistro?.maestra) {
    return maestras;
  }

  const selectedExists = maestras.some(
    (maestra) => maestra.rut === selectedRegistro.maestra,
  );

  if (selectedExists) {
    return maestras;
  }

  return [
    {
      rut: selectedRegistro.maestra,
      nombre: selectedRegistro.maestra_nombre,
      tipo: selectedRegistro.maestra_tipo,
      comuna: selectedRegistro.maestra_comuna,
      oficio: selectedRegistro.maestra_oficio,
      completion_percentage: selectedRegistro.completion_percentage ?? null,
    },
    ...maestras,
  ];
};

const formatApiError = (data) => {
  if (!data || typeof data !== "object") {
    return "No se pudo guardar el registro.";
  }

  return Object.entries(data)
    .map(([field, value]) => {
      if (Array.isArray(value)) {
        return `${field}: ${value.join(" ")}`;
      }

      return `${field}: ${value}`;
    })
    .join(" | ");
};

const RecopiladorPracticaTsPage = () => {
  const [registros, setRegistros] = useState([]);
  const [options, setOptions] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("");
  const [actualizadaFilter, setActualizadaFilter] = useState("");
  const [selectedRegistro, setSelectedRegistro] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [modalErrorMessage, setModalErrorMessage] = useState("");

  const fetchRegistros = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await fetch(getApiUrl("api/perfiles/recopilador_practica_ts/"), {
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        throw new Error("Could not load records.");
      }

      const data = await response.json();
      setRegistros(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading practica TS records:", error);
      setErrorMessage("No se pudo cargar la nómina de práctica TS.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchOptions = useCallback(async () => {
    try {
      const response = await fetch(
        getApiUrl("api/perfiles/recopilador_practica_ts/options/"),
        {
          credentials: "include",
          headers: { Accept: "application/json" },
        },
      );

      if (!response.ok) {
        throw new Error("Could not load options.");
      }

      const data = await response.json();
      setOptions(data);
    } catch (error) {
      console.error("Error loading practica TS options:", error);
      setErrorMessage("No se pudieron cargar las opciones del formulario.");
    }
  }, []);

  useEffect(() => {
    fetchRegistros();
    fetchOptions();
  }, [fetchOptions, fetchRegistros]);

  const filteredRegistros = useMemo(() => {
    const normalizedSearch = normalizeString(searchTerm);

    return registros.filter((registro) => {
      const searchText = normalizeString(
        [
          registro.maestra,
          registro.maestra_nombre,
          registro.maestra_tipo,
          registro.maestra_comuna,
          registro.maestra_oficio,
          registro.estado,
          registro.asignada_a_nombre,
          registro.trabaja_oficio,
          (registro.trabaja_oficio_no || []).join(" "),
          registro.necesidad,
          registro.trabajos_oficio,
          registro.comentarios,
        ].join(" "),
      );

      return (
        searchText.includes(normalizedSearch) &&
        (!userFilter || String(registro.asignada_a || "") === userFilter) &&
        (!estadoFilter || registro.estado === estadoFilter) &&
        (!actualizadaFilter ||
          registro.intranet_actualizada === actualizadaFilter)
      );
    });
  }, [actualizadaFilter, estadoFilter, registros, searchTerm, userFilter]);

  const stats = useMemo(() => {
    const contacted = registros.filter(
      (registro) => registro.estado === "Contactada",
    ).length;
    const updated = registros.filter(
      (registro) => registro.intranet_actualizada === "SI",
    ).length;

    return [
      { label: "Asignadas", value: registros.length, icon: <FaClipboardList /> },
      { label: "Contactadas", value: contacted, icon: <FaPhoneAlt /> },
      { label: "Actualizadas", value: updated, icon: <FaUserCheck /> },
    ];
  }, [registros]);

  const openNewModal = () => {
    setSelectedRegistro(null);
    setFormData({ ...emptyForm });
    setShowModal(true);
    setModalErrorMessage("");
  };

  const openEditModal = (registro) => {
    setSelectedRegistro(registro);
    setFormData({
      maestra: registro.maestra || "",
      estado: registro.estado || "Pendiente",
      fecha_contacto: registro.fecha_contacto || "",
      trabaja_oficio: registro.trabaja_oficio || "",
      trabaja_oficio_no: Array.isArray(registro.trabaja_oficio_no)
        ? registro.trabaja_oficio_no
        : [],
      necesidad: registro.necesidad || "",
      trabajos_oficio: registro.trabajos_oficio || "",
      intranet_actualizada: registro.intranet_actualizada || "NO",
      comentarios: registro.comentarios || "",
      asignada_a: registro.asignada_a || "",
    });
    setShowModal(true);
    setModalErrorMessage("");
  };

  const closeModal = () => {
    setSelectedRegistro(null);
    setFormData({ ...emptyForm });
    setShowModal(false);
    setIsSaving(false);
    setModalErrorMessage("");
  };

  const updateField = (field, value) => {
    setFormData((current) => {
      const next = { ...current, [field]: value };

      if (field === "trabaja_oficio" && value !== "NO") {
        next.trabaja_oficio_no = [];
      }

      return next;
    });
  };

  const toggleNoReason = (reason) => {
    setFormData((current) => {
      const selected = current.trabaja_oficio_no || [];
      const nextReasons = selected.includes(reason)
        ? selected.filter((item) => item !== reason)
        : [...selected, reason];

      return { ...current, trabaja_oficio_no: nextReasons };
    });
  };

  const saveRegistro = async () => {
    setIsSaving(true);
    setModalErrorMessage("");

    try {
      const csrfToken = await getCsrfToken();
      const isEditing = Boolean(selectedRegistro?.id);
      const url = isEditing
        ? getApiUrl(`api/perfiles/recopilador_practica_ts/${selectedRegistro.id}/`)
        : getApiUrl("api/perfiles/recopilador_practica_ts/");

      const payload = {
        ...formData,
        asignada_a: formData.asignada_a || null,
        fecha_contacto: formData.fecha_contacto || null,
        trabaja_oficio: formData.trabaja_oficio || null,
        trabaja_oficio_no: formData.trabaja_oficio_no || [],
        necesidad: formData.necesidad || null,
        trabajos_oficio: formData.trabajos_oficio || null,
      };

      const response = await fetch(url, {
        method: isEditing ? "PATCH" : "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        setModalErrorMessage(formatApiError(data));
        return;
      }

      await fetchRegistros();
      closeModal();
    } catch (error) {
      console.error("Error saving practica TS record:", error);
      setModalErrorMessage("Error de conexión al guardar el registro.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="recopilador-practica-page">
      <div className="recopilador-practica-header">
        <div>
          <h2>Coordinaci&oacute;n de Tareas</h2>
          <p>Asigna maestras, revisa responsables y da seguimiento al proceso.</p>
        </div>
        <Button
          className="recopilador-practica-add-button"
          onClick={openNewModal}
        >
          <FaPlus />
          Agregar registro
        </Button>
      </div>

      {errorMessage && (
        <Alert variant="danger" className="recopilador-practica-alert">
          {errorMessage}
        </Alert>
      )}

      <div className="recopilador-practica-stats">
        {stats.map((stat) => (
          <div key={stat.label} className="recopilador-practica-stat">
            <span>{stat.icon}</span>
            <strong>{stat.value}</strong>
            <small>{stat.label}</small>
          </div>
        ))}
      </div>

      <section className="recopilador-practica-section">
        <h5>Paso a paso</h5>
        <ol className="recopilador-practica-steps">
          {pautaPasos.map((paso, index) => (
            <li key={paso}>
              <span>{index + 1}</span>
              <p>{paso}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="recopilador-practica-section">
        <h5>Llamado telefónico</h5>
        <div className="recopilador-practica-script-grid">
          {pautaScript.map((bloque) => (
            <details key={bloque.titulo} className="recopilador-practica-script">
              <summary>{bloque.titulo}</summary>
              <p>{bloque.texto}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="recopilador-practica-section">
        <div className="recopilador-practica-table-heading">
          <h5>Nómina de maestras</h5>
          <span>{filteredRegistros.length} registros visibles</span>
        </div>

        <div className="recopilador-practica-filters">
          <Form.Control
            type="text"
            placeholder="Buscar por maestra, estado, usuario o comentario..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />

          <Form.Select
            value={userFilter}
            onChange={(event) => setUserFilter(event.target.value)}
          >
            <option value="">Todos los usuarios</option>
            {optionList(options, "users").map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            value={estadoFilter}
            onChange={(event) => setEstadoFilter(event.target.value)}
          >
            <option value="">Todos los estados</option>
            {optionList(options, "estado").map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            value={actualizadaFilter}
            onChange={(event) => setActualizadaFilter(event.target.value)}
          >
            <option value="">Intranet actualizada</option>
            {optionList(options, "intranet_actualizada").map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="recopilador-practica-table-wrap">
          {isLoading ? (
            <div className="recopilador-practica-loading">
              <Spinner animation="border" size="sm" />
              <span>Cargando nómina...</span>
            </div>
          ) : (
            <table className="recopilador-practica-table">
              <thead>
                <tr>
                  <th>Asignada a</th>
                  <th>Maestra</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                  <th>Trabaja oficio</th>
                  <th>Necesita</th>
                  <th>Trabajos realizados</th>
                  <th>Intranet</th>
                  <th>Comentarios</th>
                </tr>
              </thead>
              <tbody>
                {filteredRegistros.map((registro) => (
                  <tr
                    key={registro.id}
                    className="recopilador-practica-row"
                    onClick={() => openEditModal(registro)}
                  >
                    <td>{registro.asignada_a_nombre || "Sin asignar"}</td>
                    <td>
                      <strong>{registro.maestra_nombre || registro.maestra}</strong>
                      <span>
                        {[
                          registro.maestra_tipo,
                          registro.maestra_comuna,
                          registro.maestra_oficio,
                        ]
                          .filter(Boolean)
                          .join(" | ") || "Sin detalle"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`recopilador-practica-status ${normalizeString(
                          registro.estado,
                        ).replace(/\s+/g, "-")}`}
                      >
                        {registro.estado}
                      </span>
                    </td>
                    <td>{registro.fecha_contacto || "-"}</td>
                    <td>
                      {registro.trabaja_oficio_si
                        ? "SI"
                        : registro.trabaja_oficio === "SI"
                          ? "SI"
                          : registro.trabaja_oficio === "NO"
                            ? `NO${
                                registro.trabaja_oficio_no?.length
                                  ? `: ${registro.trabaja_oficio_no.join(", ")}`
                                  : ""
                              }`
                            : "-"}
                    </td>
                    <td>{registro.necesidad || "-"}</td>
                    <td>{registro.trabajos_oficio || "-"}</td>
                    <td>{registro.intranet_actualizada || "-"}</td>
                    <td>{registro.comentarios || "-"}</td>
                  </tr>
                ))}

                {filteredRegistros.length === 0 && (
                  <tr>
                    <td colSpan="9" className="recopilador-practica-empty">
                      No hay registros para mostrar.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <Modal
        show={showModal}
        onHide={closeModal}
        size="lg"
        centered
        className="recopilador-practica-modal"
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title>
              {selectedRegistro ? "Editar registro" : "Agregar registro"}
            </Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          {modalErrorMessage && (
            <Alert variant="danger" className="recopilador-practica-alert">
              {modalErrorMessage}
            </Alert>
          )}

          <div className="recopilador-practica-form-grid">
            <Form.Group className="recopilador-practica-form-field wide">
              <Form.Label>Maestra</Form.Label>
              <Form.Select
                value={formData.maestra}
                disabled={Boolean(selectedRegistro)}
                onChange={(event) => updateField("maestra", event.target.value)}
              >
                <option value="">Seleccionar maestra</option>
                {getSelectableMaestras(options, selectedRegistro).map((maestra) => (
                  <option key={maestra.rut} value={maestra.rut}>
                    {formatMaestraOptionLabel(maestra)}
                    {maestra.comuna ? ` | ${maestra.comuna}` : ""}
                    {maestra.oficio ? ` | ${maestra.oficio}` : ""}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="recopilador-practica-form-field">
              <Form.Label>Asignada a</Form.Label>
              <Form.Select
                value={formData.asignada_a}
                onChange={(event) => updateField("asignada_a", event.target.value)}
              >
                <option value="">Sin asignar</option>
                {optionList(options, "users").map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="recopilador-practica-form-field">
              <Form.Label>Estado</Form.Label>
              <Form.Select
                value={formData.estado}
                onChange={(event) => updateField("estado", event.target.value)}
              >
                {optionList(options, "estado").map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="recopilador-practica-form-field">
              <Form.Label>Fecha contacto</Form.Label>
              <Form.Control
                type="date"
                value={formData.fecha_contacto}
                onChange={(event) =>
                  updateField("fecha_contacto", event.target.value)
                }
              />
            </Form.Group>

            <Form.Group className="recopilador-practica-form-field">
              <Form.Label>Esta trabajando en el oficio</Form.Label>
              <Form.Select
                value={formData.trabaja_oficio}
                onChange={(event) =>
                  updateField("trabaja_oficio", event.target.value)
                }
              >
                <option value="">---------</option>
                {optionList(options, "trabaja_oficio").map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {formData.trabaja_oficio === "NO" && (
              <Form.Group className="recopilador-practica-form-field wide">
                <Form.Label>Razones por las que no trabaja en el oficio</Form.Label>
                <div className="recopilador-practica-check-grid">
                  {optionList(options, "trabaja_oficio_no").map((option) => (
                    <Form.Check
                      key={option.value}
                      type="checkbox"
                      label={option.label}
                      checked={(formData.trabaja_oficio_no || []).includes(
                        option.value,
                      )}
                      onChange={() => toggleNoReason(option.value)}
                    />
                  ))}
                </div>
              </Form.Group>
            )}

            <Form.Group className="recopilador-practica-form-field">
              <Form.Label>Necesita para desarrollarse</Form.Label>
              <Form.Select
                value={formData.necesidad}
                onChange={(event) => updateField("necesidad", event.target.value)}
              >
                <option value="">---------</option>
                {optionList(options, "necesidad").map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="recopilador-practica-form-field">
              <Form.Label>Trabajos realizados</Form.Label>
              <Form.Select
                value={formData.trabajos_oficio}
                onChange={(event) =>
                  updateField("trabajos_oficio", event.target.value)
                }
              >
                <option value="">---------</option>
                {optionList(options, "trabajos_oficio").map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="recopilador-practica-form-field">
              <Form.Label>Información actualizada en Intranet</Form.Label>
              <Form.Select
                value={formData.intranet_actualizada}
                onChange={(event) =>
                  updateField("intranet_actualizada", event.target.value)
                }
              >
                {optionList(options, "intranet_actualizada").map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="recopilador-practica-form-field wide">
              <Form.Label>Comentarios</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={formData.comentarios}
                onChange={(event) =>
                  updateField("comentarios", event.target.value)
                }
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="recopilador-practica-cancel-button"
            onClick={closeModal}
            disabled={isSaving}
          >
            <FaTimes />
            Cancelar
          </Button>
          <Button
            className="recopilador-practica-save-button"
            onClick={saveRegistro}
            disabled={isSaving}
          >
            <FaSave />
            {isSaving ? "Guardando..." : "Guardar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RecopiladorPracticaTsPage;
